import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { mintMoreSupply } from "../../../lib/investAdminApi";
import { ConfirmChainDialog, fmtNum, fmtUsd } from "./bcShared";

const PRESETS = [100, 500, 1000, 5000];

export default function MintSupplyDialog({ collection, paused, onOpenChange, onMinted }) {
  const [amount, setAmount] = useState("500");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (collection) setAmount("500"); }, [collection]);

  if (!collection) return null;
  const n = Number(amount);
  const valid = Number.isInteger(n) && n >= 1 && n <= 1_000_000_000;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await mintMoreSupply(collection.id, n);
      toast.success(`Minted ${fmtNum(n)} ${collection.symbol}`);
      onOpenChange(false);
      onMinted();
    } catch (e) {
      toast.error(e.message || "Mint failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmChainDialog
      open={!!collection}
      onOpenChange={onOpenChange}
      title={`Mint more ${collection.symbol}`}
      description={`New tokens go to the admin wallet and are listed for sale at ${fmtUsd(collection.unitPriceCents)} each. Supply can't be reduced later.`}
      confirmLabel={valid ? `Mint ${fmtNum(n)} tokens` : "Mint tokens"}
      busy={busy}
      onConfirm={submit}
    >
      <div className="space-y-4">
        {paused && (
          <div className="text-xs p-3 rounded-lg bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]" role="alert">
            The contract is paused — minting will fail until you resume it.
          </div>
        )}
        <div>
          <label htmlFor="bc-mint-amount" className="block text-xs font-semibold mb-1.5">Tokens to mint</label>
          <input
            id="bc-mint-amount"
            inputMode="numeric"
            className={"zc-input w-full h-10 px-3 tabular-nums" + (!valid && amount ? " !border-[var(--zc-danger)]" : "")}
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))}
            aria-invalid={!valid}
            autoFocus
          />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => setAmount(String(p))} className="zc-chip px-2.5 h-7 text-xs rounded-full border border-[var(--zc-border)]">
                +{fmtNum(p)}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-xl bg-[var(--zc-bg-3)] p-3 text-xs space-y-1.5 tabular-nums">
          <div className="flex justify-between items-center">
            <span className="text-[var(--zc-text-3)]">Total supply</span>
            <span className="inline-flex items-center gap-1.5">
              {fmtNum(collection.totalSupply)} <ArrowRight size={11} /> <b>{fmtNum((collection.totalSupply || 0) + (valid ? n : 0))}</b>
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[var(--zc-text-3)]">Available for sale</span>
            <span className="inline-flex items-center gap-1.5">
              {fmtNum(collection.availableForSale)} <ArrowRight size={11} /> <b>{fmtNum((collection.availableForSale || 0) + (valid ? n : 0))}</b>
            </span>
          </div>
        </div>
      </div>
    </ConfirmChainDialog>
  );
}
